import * as _ from 'es-toolkit/compat';
import { DefaultSearchQueryStringParams, setDefaultSearchQueryParams } from 'utils/citySearchUtils';
import { PLACEHOLDER_CUISINES_SET } from 'components/www/availability-search/filters/CuisineFilterButton';
import {
  SEARCH_BAR_SECTION_DIVIDER,
  SearchBarOption,
  SearchBarOptionHeading,
  SearchBarOptionOrSectionDivider,
  SearchBarOptionQuery,
  SearchBarOptionText,
  SearchNavEntityTypes,
  recommendedQueries,
} from 'utils/searchBar';
import { SearchParams } from 'actions/AvailabilityActions';
import { consumer, core, safeEnumCompare } from '@tock/coretypes';
import { createSelector } from 'reselect';
import {
  currentMetroAreaSelector,
  featuredCitiesSelector,
  getNearbyMetroSelector,
} from 'selectors/metroAreaSelectors';
import { displayLocation } from '@tock/shared/utils/locationUtils';
import { getCurrentSearch, getQueryResultMatches } from 'selectors/searchSelectors';
import { getFeatures } from 'selectors/featuresSelector';
import Fuse from 'fuse.js';
import protos from 'utils/protos';
import queryString from '@tock/query-string';

const { MetaTag } = protos;

export const EMPTY_SEARCH_HEADING: SearchBarOptionHeading = {
  primaryText: 'Popular searches',
  category: SearchNavEntityTypes.HEADING,
};
export const CITY_HEADING: SearchBarOptionHeading = {
  primaryText: 'Cities',
  category: SearchNavEntityTypes.HEADING,
};
export const CUISINE_HEADING: SearchBarOptionHeading = {
  primaryText: 'Cuisines',
  category: SearchNavEntityTypes.HEADING,
};
export const DESTINATION_HEADING: SearchBarOptionHeading = {
  primaryText: 'Destinations',
  category: SearchNavEntityTypes.HEADING,
};

function fuzzySearchPromos(
  searchPromos: SearchBarOptionQuery[],
  trimmedSearch: string
): SearchBarOptionQuery[] {
  const fuse = new Fuse(searchPromos, {
    keys: ['primaryText'],
    minMatchCharLength: 3,
    threshold: 0.4,
  });

  const fuseResults = fuse.search(trimmedSearch);
  return fuseResults.map((result) => result.item);
}

const buildQueryStringParams = ({
  date,
  time,
  size,
  type,
  city,
  q,
  cuisines,
}: Partial<SearchParams>): DefaultSearchQueryStringParams => {
  const query: DefaultSearchQueryStringParams = {};
  if (date) {
    query.date = date.toString();
  }
  if (size) {
    query.size = _.toString(size);
  }
  if (time) {
    query.time = time.toString();
  }
  if (query.type) {
    query.type = type;
  }
  if (city) {
    // city in the second part of this scenario is now passed in the search results page, but not in the initial search.
    query.city = city;
  }
  if (q) {
    query.q = q;
  }
  if (cuisines) {
    query.cuisines = cuisines;
  }

  return query;
};

const buildQueryString = (
  currentSearch: string,
  currentMetroArea: core.MetroArea,
  enableCuisineFilter?: boolean
) => {
  const defaultQueryParams = setDefaultSearchQueryParams(
    PLACEHOLDER_CUISINES_SET.has(currentSearch.toLowerCase()) && enableCuisineFilter
      ? {
          city: currentMetroArea?.name,
          cuisines: [currentSearch],
        }
      : {
          city: currentMetroArea?.name,
          q: currentSearch,
        },
    currentMetroArea.timezone
  );

  const defaultQueryStringParams = buildQueryStringParams(defaultQueryParams);

  const defaultQueryStringParamsString = queryString.stringify(
    defaultQueryStringParams as Dictionary<string>
  );

  return defaultQueryStringParamsString;
};

export function toMetroAreaOptions(
  results: consumer.NavSuggestResponse.IMetroAreaSuggestion[],
  nearbyMetroArea?: core.MetroArea
): SearchBarOption[] {
  // OrderBy puts the nearby city at the top of the list of cities and alphabetizes the rest
  const orderedMetroAreas = _.orderBy(
    results,
    [
      (city) => {
        return city.name === (nearbyMetroArea?.name ?? '');
      },
      'name',
    ],
    ['desc', 'asc']
  );

  const metroAreaOptions: SearchBarOption[] = _.map(orderedMetroAreas, (metro) => {
    return {
      primaryText: `${metro.name}`,
      secondaryText: metro.name === nearbyMetroArea?.name ? 'Nearby' : '',
      href: `/city/${metro?.slug}?tock_source=tock&tock_medium=search_nav`,
      category: SearchNavEntityTypes.CITY,
      metroArea: metro,
    };
  });
  return metroAreaOptions;
}

export function toCuisineTagOptions(
  results: consumer.IMetaTagSuggestion[],
  selectedMetroArea: core.MetroArea
): SearchBarOption[] {
  const metroAreaOptions: SearchBarOption[] = _.map(results, (metaTag) => {
    const defaultQueryStringParams = buildQueryString(metaTag.slug || '', selectedMetroArea);
    return {
      primaryText: `${metaTag.name}`,
      href: `/city/${selectedMetroArea?.slug}/search?${defaultQueryStringParams}&tock_source=tock&tock_medium=search_nav`,
      category: SearchNavEntityTypes.CUISINE,
    };
  });
  return metroAreaOptions;
}

export function toTextOptions(
  results: string[],
  currentMetroArea?: core.MetroArea
): SearchBarOption[] {
  const orderedOptions = Array.from(results).sort();
  const options: SearchBarOption[] = orderedOptions.map((item) => {
    const query = item.toLowerCase();
    let defaultQueryStringParams = '';
    if (currentMetroArea) {
      defaultQueryStringParams = buildQueryString(query, currentMetroArea);
    }

    return {
      primaryText: item,
      href: `/city/${currentMetroArea?.slug}/search?${defaultQueryStringParams}&tock_source=tock&tock_medium=search_nav`,
      category: SearchNavEntityTypes.QUERY,
    };
  });
  return options;
}

export function toBusinessOptions(
  businessOptions: consumer.NavSuggestResponse.IBusinessSuggestion[],
  currentSearch: string
): SearchBarOption[] {
  // Re-order the business entries to match by prioritizing matches that have the match criteria in the name in entry,
  // entries that don't go at the end of the list
  const matchedBusinessEntries: consumer.NavSuggestResponse.IBusinessSuggestion[] = _.filter(
    businessOptions,
    (entry: consumer.NavSuggestResponse.IBusinessSuggestion) => {
      return _.toLower(`${entry.name} ${entry.city}`).indexOf(currentSearch) >= 0;
    }
  ).concat(
    _.filter(businessOptions, (entry: consumer.NavSuggestResponse.IBusinessSuggestion) => {
      return _.toLower(`${entry.name} ${entry.city}`).indexOf(currentSearch) < 0;
    })
  );
  return _.map(
    matchedBusinessEntries,
    (business: consumer.NavSuggestResponse.IBusinessSuggestion): SearchBarOption => {
      return {
        primaryText: `${business.name}`,
        secondaryText: `${displayLocation(business)}${
          business.neighborhood ? ` - ${business.neighborhood}` : ''
        }`,
        href: `/${business.domainName}?tock_source=tock&tock_medium=search_nav`,
        category: SearchNavEntityTypes.BUSINESS,
        business,
      };
    }
  );
}

export function resultsWithHeading(
  heading: SearchBarOption,
  options: SearchBarOption[]
): SearchBarOption[] {
  if (_.isEmpty(options)) {
    return [];
  } else {
    return [heading, ...options];
  }
}

function promo(path: string, primaryText: string): SearchBarOptionQuery {
  return {
    category: SearchNavEntityTypes.QUERY,
    href: `${path}?tock_source=tock&tock_medium=search_promo`,
    primaryText,
  };
}

const THANKSGIVING_PROMO = promo('/c/thanksgiving', 'Thanksgiving');
const HOLIDAY_PROMO = promo('/c/holidays', 'Holidays');
const NYE_PROMO = promo('/c/nye', "New Year's Eve");
const VALENTINES_PROMO = promo('/c/valentines', "Valentine's Day");
const EASTER_PROMO = promo('/c/easter', 'Easter');
const PASSOVER_PROMO = promo('/collections/passover', 'Passover');
const MOTHERS_DAY_PROMO = promo('/c/mothersday', "Mother's Day weekend");
const FATHERS_DAY_PROMO = promo('/c/fathersday', "Father's Day weekend");
const MEMORIAL_DAY_PROMO = promo('/c/memorialday', 'Memorial Day weekend');
const FOURTH_OF_JULY_PROMO = promo('/c/fourthofjuly', 'Fourth of July');
const LABOR_DAY_PROMO = promo('/c/laborday', 'Labor Day');

const candidateSearchPromos: [SearchBarOptionQuery, core.Feature.FeatureName | undefined][] = [
  [THANKSGIVING_PROMO, 'POPULAR_SEARCH_THANKSGIVING_PROMO'],
  [HOLIDAY_PROMO, 'POPULAR_SEARCH_HOLIDAYS_PROMO'],
  [NYE_PROMO, 'POPULAR_SEARCH_NYE_PROMO'],
  [VALENTINES_PROMO, 'POPULAR_SEARCH_VALENTINES_PROMO'],
  [EASTER_PROMO, 'POPULAR_SEARCH_EASTER_PROMO'],
  [PASSOVER_PROMO, 'POPULAR_SEARCH_PASSOVER_PROMO'],
  [MOTHERS_DAY_PROMO, 'POPULAR_SEARCH_MOTHERS_DAY_PROMO'],
  [FATHERS_DAY_PROMO, 'POPULAR_SEARCH_FATHERS_DAY_PROMO'],
  [MEMORIAL_DAY_PROMO, 'POPULAR_SEARCH_MEMORIAL_DAY_PROMO'],
  [FOURTH_OF_JULY_PROMO, 'POPULAR_SEARCH_FOURTH_OF_JULY_PROMO'],
  [LABOR_DAY_PROMO, 'POPULAR_SEARCH_LABOR_DAY_PROMO'],
];

const promosSelector = createSelector(getFeatures, (features) => {
  return candidateSearchPromos
    .filter(([_, flag]) => {
      return !flag || features[flag];
    })
    .map(([item, _]) => item);
});

const searchBarSuggestionsSelector = createSelector(
  getQueryResultMatches,
  getCurrentSearch,
  featuredCitiesSelector,
  getNearbyMetroSelector,
  currentMetroAreaSelector,
  promosSelector,
  getFeatures,
  (
    matches,
    currentSearch,
    featuredCities,
    nearbyMetroArea,
    currentMetroArea,
    searchPromos,
    features
  ): SearchBarOption[] => {
    const {
      business: businessMatches,
      metroArea: metroAreaMatches,
      metaTag: metaTagMatches,
    } = matches;
    const trimmedSearch = currentSearch.trim();

    const fuzzySearchResults = fuzzySearchPromos(searchPromos, trimmedSearch);

    // Use the list of featured cities if there is no currentSearch otherwise use the matches
    const selectedResults = trimmedSearch ? metroAreaMatches : featuredCities;
    // const metroAreaOptions = toMetroAreaOptions(selectedResults ?? [], nearbyMetroArea);
    const metroAreaOptions = trimmedSearch
      ? toMetroAreaOptions(selectedResults ?? [], nearbyMetroArea)
      : toTextOptions(recommendedQueries ?? [], currentMetroArea);
    const businessOptions = toBusinessOptions(businessMatches, trimmedSearch);

    const cuisineTagMatches = metaTagMatches
      .filter((tag) => safeEnumCompare(tag.tagType, MetaTag.TagType, MetaTag.TagType.CUISINE))
      .filter((tag) => PLACEHOLDER_CUISINES_SET.has(tag.name?.toLowerCase() || ''));
    const cuisineOptions = currentMetroArea
      ? toCuisineTagOptions(cuisineTagMatches, currentMetroArea)
      : [];

    const defaultQueryStringParamsString = currentMetroArea
      ? buildQueryString(trimmedSearch, currentMetroArea)
      : '';

    let results: SearchBarOption[];
    let seeAllOption: SearchBarOptionText[];
    if (trimmedSearch && trimmedSearch.length > 0 && currentMetroArea) {
      seeAllOption = [
        {
          primaryText: `See results for "${trimmedSearch}" in ${currentMetroArea?.name}`,
          category: SearchNavEntityTypes.TEXT,
          href: `/city/${currentMetroArea.slug}?${defaultQueryStringParamsString}&tock_source=tock&tock_medium=search_nav`,
        },
      ];
    } else {
      seeAllOption = [];
    }

    if (trimmedSearch === '') {
      results = [
        {
          primaryText: `All businesses in ${currentMetroArea?.name}`,
          category: SearchNavEntityTypes.TEXT,
          href: `/city/${currentMetroArea?.slug}?${defaultQueryStringParamsString}&tock_source=tock&tock_medium=search_nav`,
        },
        EMPTY_SEARCH_HEADING,
        ...searchPromos,
        ...metroAreaOptions,
      ];
    } else {
      results = [
        ...resultsWithHeading(CUISINE_HEADING, cuisineOptions),
        ...resultsWithHeading(CITY_HEADING, metroAreaOptions),
        ...resultsWithHeading(DESTINATION_HEADING, businessOptions),
      ];
    }

    if (fuzzySearchResults.length > 0) {
      results = [EMPTY_SEARCH_HEADING, ...fuzzySearchResults, ...results];
    }

    if (seeAllOption) {
      results = [...seeAllOption, ...results];
    }
    return results;
  }
);

export default searchBarSuggestionsSelector;

export const searchBarSuggestionsSectionedSelector = createSelector(
  getQueryResultMatches,
  getCurrentSearch,
  featuredCitiesSelector,
  getNearbyMetroSelector,
  currentMetroAreaSelector,
  promosSelector,
  getFeatures,
  (
    matches,
    currentSearch,
    featuredCities,
    nearbyMetroArea,
    currentMetroArea,
    searchPromos,
    features
  ): SearchBarOptionOrSectionDivider[] => {
    const {
      business: businessMatches,
      metroArea: metroAreaMatches,
      metaTag: metaTagMatches,
    } = matches;
    const trimmedSearch = currentSearch.trim();

    const fuzzySearchResults = fuzzySearchPromos(searchPromos, trimmedSearch);

    // Use the list of featured cities if there is no currentSearch otherwise use the matches
    const selectedResults = trimmedSearch ? metroAreaMatches : featuredCities;
    // const metroAreaOptions = toMetroAreaOptions(selectedResults ?? [], nearbyMetroArea);
    const metroAreaOptions = trimmedSearch
      ? toMetroAreaOptions(selectedResults ?? [], nearbyMetroArea)
      : toTextOptions(recommendedQueries ?? [], currentMetroArea);
    const businessOptions = toBusinessOptions(businessMatches, trimmedSearch);

    const cuisineTagMatches = metaTagMatches.filter(
      (tag) =>
        safeEnumCompare(tag.tagType, MetaTag.TagType, MetaTag.TagType.CUISINE) &&
        PLACEHOLDER_CUISINES_SET.has(tag.name?.toLowerCase() || '')
    );
    const cuisineOptions = currentMetroArea
      ? toCuisineTagOptions(cuisineTagMatches, currentMetroArea)
      : [];

    const defaultQueryStringParamsString = currentMetroArea
      ? buildQueryString(trimmedSearch, currentMetroArea)
      : '';

    const results: SearchBarOptionOrSectionDivider[] = [];
    if (trimmedSearch && currentMetroArea) {
      results.push(
        {
          primaryText: `See results for "${trimmedSearch}" in ${currentMetroArea?.name}`,
          category: SearchNavEntityTypes.TEXT,
          href: `/city/${currentMetroArea.slug}?${defaultQueryStringParamsString}&tock_source=tock&tock_medium=search_nav`,
        },
        SEARCH_BAR_SECTION_DIVIDER
      );
    }

    if (fuzzySearchResults.length || trimmedSearch) {
      if (fuzzySearchResults.length) {
        results.push(EMPTY_SEARCH_HEADING, ...fuzzySearchResults);
      }
      if (trimmedSearch) {
        results.push(
          ...resultsWithHeading(CUISINE_HEADING, cuisineOptions),
          ...resultsWithHeading(CITY_HEADING, metroAreaOptions),
          ...resultsWithHeading(DESTINATION_HEADING, businessOptions)
        );
      }
    } else {
      results.push(
        {
          primaryText: `All businesses in ${currentMetroArea?.name}`,
          category: SearchNavEntityTypes.TEXT,
          href: `/city/${currentMetroArea?.slug}?${defaultQueryStringParamsString}&tock_source=tock&tock_medium=search_nav`,
        },
        SEARCH_BAR_SECTION_DIVIDER,
        EMPTY_SEARCH_HEADING,
        ...searchPromos,
        ...metroAreaOptions
      );
    }

    return results;
  }
);

/**
 * exported for testing
 */
export const citySuggestions = (searchBarOptions: SearchBarOption[]): SearchBarOption[] =>
  searchBarOptions.filter(
    (option) =>
      option.category === SearchNavEntityTypes.CITY ||
      (option.category === SearchNavEntityTypes.HEADING && option.primaryText !== 'Destinations')
  );

export const citySuggestionsSelector = createSelector(
  searchBarSuggestionsSelector,
  citySuggestions
);
